import { useCallback, useMemo, useState } from "react";
import { EmptyArray } from "../../utilities/EmptyArray";
import { EmptyObject } from "../../utilities/EmptyObject";
import { Filterable, FilterMethods, FilterSet, FilterValue } from "../Filterable";
import { Sortable, SortDirection, SortMethods } from "../Sortable";
import { Batchable } from "../Batchable";
import { DataSourceSortEntry } from "./DataSourceSortEntry";
import stringify from "json-stable-stringify";

/**
 * The data source state.
 */
interface State {
  /**
   * The current sort.
   */
  readonly sort: ReadonlyArray<DataSourceSortEntry>;

  /**
   * The current filter.
   */
  readonly filter: FilterSet;
}

/**
 * Provides a remote table data source.
 * @params initialState The initial state.
 * @returns A table data source which provides access to the row data.
 */
export function useSharedDataSourceFunctions(initialState: State): State & Filterable & Sortable & Batchable<FilterMethods & SortMethods> {
  const [state, setState] = useState<State>(
    initialState || {
      sort: EmptyArray as ReadonlyArray<DataSourceSortEntry>,
      filter: EmptyObject as FilterSet,
    },
  );

  const sortBy = useCallback((name: string, direction: SortDirection | null, exclusive = true) => {
    setState((current) => {
      let { sort } = current;

      if (exclusive) {
        if (direction !== null) sort = [{ name, direction }];
        else sort = EmptyArray;
      } else {
        if (direction === null) sort = sort.filter((entry) => entry.name !== name);
        else {
          const entry = sort.find((entry) => entry.name === name);
          if (entry) {
            sort = sort.map((entry) => (entry.name === name ? { name, direction } : entry));
          } else {
            sort = [...sort, { name, direction }];
          }
        }
      }

      if (stringify(sort) === stringify(current.sort)) return current;
      return { ...current, sort };
    });
  }, []);

  const getSortDirection = useCallback(
    (name: string) => {
      return state.sort.find((entry) => entry.name === name)?.direction ?? null;
    },
    [stringify(state.sort)],
  );

  const clearSort = useCallback(() => {
    setState((current) => {
      if (stringify(current.sort) === stringify(EmptyArray)) return current;

      return {
        ...current,
        sort: EmptyArray as ReadonlyArray<DataSourceSortEntry>,
      };
    });
  }, []);

  const setFilter = useCallback((filter: FilterSet) => {
    doBatch(({ clearFilter, filterBy }) => {
      clearFilter();

      for (const entry of Object.entries(filter)) {
        const [key, value] = entry;
        if (!key || typeof value === "undefined" || value === null || value === "") continue;

        filterBy(key, value);
      }
    });
  }, []);

  const filterBy = useCallback((name: string, value: FilterValue) => {
    if (name === "page_number") throw new Error("Filter on 'page_number' not allowed on remote data source.");
    if (name === "results_per_page") throw new Error("Filter on 'results_per_page' not allowed on remote data source.");
    if (name === "order_by") throw new Error("Filter on 'order_by' not allowed on remote data source.");
    if (name === "full_text_search" && value !== null && typeof value !== "string")
      throw new Error("Filter for 'full_text_search' must be a string.");

    setState((current) => {
      let { filter } = current;

      if (filter[name] === null && value === null) return current;
      if (filter[name] === value) return current;

      if (value === null) {
        const { [name]: _, ...rest } = filter;
        filter = rest;
      } else {
        filter = { ...filter, [name]: value };
      }

      return { ...current, filter };
    });
  }, []);

  const getFilter = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (name?: string): any => {
      if (typeof name === "undefined") return state.filter;
      return state.filter[name] ?? null;
    },
    [stringify(state.filter)],
  );

  const clearFilter = useCallback(() => {
    setState((current) => {
      if (stringify(current.filter) === stringify(EmptyObject)) return current;

      return {
        ...current,
        filter: EmptyObject as FilterSet,
      };
    });
  }, []);

  const doBatch = useCallback((batch: (state: FilterMethods & SortMethods) => void) => {
    setState((current) => {
      let { filter, sort } = current;

      const clearFilter = () => {
        filter = EmptyObject as FilterSet;
      };

      const clearSort = () => {
        sort = EmptyArray;
      };

      const filterBy = (name: string, value: FilterValue) => {
        if (name === "page_number") throw new Error("Filter on 'page_number' not allowed on remote data source.");
        if (name === "results_per_page") throw new Error("Filter on 'results_per_page' not allowed on remote data source.");
        if (name === "order_by") throw new Error("Filter on 'order_by' not allowed on remote data source.");
        if (name === "full_text_search" && value !== null && typeof value !== "string")
          throw new Error("Filter for 'full_text_search' must be a string.");

        if (filter[name] === null && value === null) return;
        if (filter[name] === value) return;

        if (value === null) {
          const { [name]: _, ...rest } = filter;
          filter = rest;
        } else {
          filter = { ...filter, [name]: value };
        }
      };

      batch({
        setFilter: () => {
          clearFilter();
          for (const entry of Object.entries(filter)) {
            const [key, value] = entry;
            if (!key || typeof value === "undefined" || value === null || value === "") continue;

            filterBy(key, value);
          }
        },
        clearFilter,
        filterBy,
        clearSort,
        sortBy: (name, direction, exclusive) => {
          if (exclusive) {
            if (direction !== null) sort = [{ name, direction }];
            else sort = EmptyArray;
          } else {
            if (direction === null) sort = sort.filter((entry) => entry.name !== name);
            else {
              const entry = sort.find((entry) => entry.name === name);
              if (entry) {
                sort = sort.map((entry) => (entry.name === name ? { name, direction } : entry));
              } else {
                sort = [...sort, { name, direction }];
              }
            }
          }
        },
      });

      if (stringify(filter) !== stringify(current.filter)) {
        current = { ...current, filter };
      }

      if (stringify(sort) !== stringify(current.sort)) {
        current = { ...current, sort };
      }

      return current;
    });
  }, []);

  return useMemo((): State & Filterable & Sortable & Batchable<FilterMethods & SortMethods> => {
    const { sort } = state;

    return {
      ...state,
      sorted: sort.length > 0,
      clearSort,
      sortBy,
      getSortDirection,
      setFilter,
      filterBy,
      getFilter,
      clearFilter,
      doBatch,
    };
  }, [state, getSortDirection, getFilter]);
}
